import { Pipe, PipeTransform } from '@angular/core';
import { Translation } from './../ridango.models';

const NO_MATCH = Symbol('no-match');
type Resolved = string | typeof NO_MATCH;

@Pipe({
    name: 'translateObjectField'
})
export class TranslateObjectFieldPipe implements PipeTransform {
    transform(object: any, field: string, language: string, fallbackField?: string): string {
        if (!object) {
            return null;
        }

        if (typeof object === 'string') {
            return object;
        }

        if (this.isPlainTranslationlessObject(object)) {
            const result = this.resolveFromPlainObject(object, language, fallbackField);
            if (result !== NO_MATCH) return result;
        }

        if ('translations' in object) {
            const result = this.resolveFromTranslationsProperty(object, field, language);
            if (result !== NO_MATCH) return result;
        }

        if (field in object) {
            const result = this.resolveFromFieldProperty(object, field, language);
            if (result !== NO_MATCH) return result;
        }

        return this.resolveFallback(object, field, language, fallbackField);
    }

    private isPlainTranslationlessObject(object: any): boolean {
        return (
            typeof object === 'object' &&
            !Array.isArray(object) &&
            !('translations' in object) &&
            !('productTranslations' in object)
        );
    }

    private resolveFromPlainObject(object: any, language: string, fallbackField?: string): Resolved {
        if (object[language]) return object[language];
        if (object.en) return object.en;

        if (Object.prototype.hasOwnProperty.call(object, 'translation_' + language)) {
            return object['translation_' + language];
        }

        if (typeof fallbackField === 'string' && fallbackField in object) {
            return object[fallbackField];
        }

        const firstKey = Object.keys(object)[0];
        if (firstKey) return object[firstKey];

        return NO_MATCH;
    }

    private resolveFromTranslationsProperty(object: any, field: string, language: string): Resolved {
        if (Array.isArray(object.translations)) {
            return this.resolveFromTranslationsArray(object.translations, field, language);
        }

        if (typeof object.translations === 'object' && !Array.isArray(object.translations)) {
            return this.resolveFromTranslationsMap(object.translations, language);
        }

        return NO_MATCH;
    }

    private resolveFromTranslationsArray(translations: Translation[], field: string, language: string): Resolved {
        const translation: Translation = translations.find((t: Translation) => {
            if (t.field) {
                return t.field === field && t.lang === language;
            }
            return t.lang === language;
        });

        if (translation) {
            return translation.translation || translation[field];
        }

        if (translations.length > 0) {
            const firstTranslation = translations[0];
            return firstTranslation.translation || firstTranslation[field];
        }

        return NO_MATCH;
    }

    private resolveFromTranslationsMap(translations: any, language: string): Resolved {
        if (translations[language]) return translations[language];
        if (translations.en) return translations.en;

        const firstKey = Object.keys(translations)[0];
        if (firstKey) return translations[firstKey];

        return NO_MATCH;
    }

    private resolveFromFieldProperty(object: any, field: string, language: string): Resolved {
        if (typeof object[field] === 'string') {
            return object[field];
        }

        if (Array.isArray(object[field])) {
            return this.resolveFromFieldArray(object[field], field, language);
        }

        return NO_MATCH;
    }

    private resolveFromFieldArray(items: Translation[], field: string, language: string): Resolved {
        const translation: Translation = items.find((t: Translation) => t.lang === language);

        if (translation) {
            return translation.translation || translation[field];
        }

        if (items.length > 0) {
            const firstTranslation = items[0];
            return firstTranslation.translation || firstTranslation[field];
        }

        return NO_MATCH;
    }

    private resolveFallback(object: any, field: string, language: string, fallbackField?: string): string {
        if (Object.prototype.hasOwnProperty.call(object, 'translation_' + language)) {
            return object['translation_' + language];
        }

        if (Object.prototype.hasOwnProperty.call(object, field + '_' + language)) {
            return object[field + '_' + language];
        }

        if (typeof fallbackField === 'string' && fallbackField in object) {
            return object[fallbackField];
        }

        if (Object.prototype.hasOwnProperty.call(object, 'product_name')) {
            return object.product_name;
        }

        return null;
    }
}
